import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table } from 'antd';

import styles from './styles.module.scss';
import TemperatureIcon from '../../assets/icons/metrics/metric-temperature.png';
import DewDurationIcon from '../../assets/icons/metrics/metric-dew-duration.png';
import DewIcon from '../../assets/icons/metrics/metric-dew.png';
import HumidityIcon from '../../assets/icons/metrics/metric-humidity.png';
import GddIcon from '../../assets/icons/metrics/metric-gdd.png';
import NdviIcon from '../../assets/icons/metrics/metric-ndvi.png';
import SoilTemperatureIcon from '../../assets/icons/metrics/metric-soil-temperature.png';
import SoilMoistureIcon from '../../assets/icons/metrics/metric-soil-moisture.png';

import { MetricCard } from './index.jsx';
import { convertSecondsToHoursAndMinutes, convertSecondsToHoursAndMinutesArray } from '../../utils/dates';
import { useViewport } from '../../hooks';
import { BreakpointMobile, BreakpointMobileUp } from '../../constants/ui';
import { GetMetricsColumns } from './libs/index.jsx';
import { getMetricsLabels } from '../../utils/metrics';
import { selectActiveReportChart } from '../../store/slices/sensors';

const Metrics = ({ t, sensor, sensorCompared, showSoilData }) => {
  const dispatch = useDispatch();
  const { selectedSecondarySensor, activeReportChart } = useSelector((state) => state.sensors);
  const [data, setData] = useState([]);
  const { widthViewport } = useViewport();
  const noDataLabel = t('empty.noData');
  const MetricsColumns = GetMetricsColumns(t);
  const MetricsLabels = getMetricsLabels(t);

  const onClickCard = (id) => {
    dispatch(selectActiveReportChart(id));
  }

  useEffect(() => {
    if (!sensor || (selectedSecondarySensor && (!sensorCompared || !sensorCompared.id))) return;

    const sensors = selectedSecondarySensor ? [sensor, sensorCompared] : [sensor];
    const dataSource = sensors.reduce((acc, s) => {
      if (s) {
        const {
          id,
          location,
          temperature,
          temperatureMax,
          temperatureAvg,
          temperatureMin,
          gdd,
          ndvi,
          dewDuration,
          dew,
          humidity,
          soilMoisture,
          soilTemperature,
        } = s;

        const minMaxValuesDewDuration =
          dewDuration || dewDuration === 0 ? convertSecondsToHoursAndMinutesArray(dewDuration) : null;

        let dewDurationValue;
        let dewDurationSecondaryValue;
        if (dewDuration || dewDuration === 0) {
          if (widthViewport > BreakpointMobile) {
            dewDurationValue = convertSecondsToHoursAndMinutes(dewDuration);
          } else if (minMaxValuesDewDuration && minMaxValuesDewDuration[0]) {
            dewDurationValue = `${minMaxValuesDewDuration[0]} h`;
          }
          if (widthViewport < BreakpointMobileUp && minMaxValuesDewDuration && minMaxValuesDewDuration[1]) {
            dewDurationSecondaryValue = `${minMaxValuesDewDuration[1]} min`;
          }
        }

        let soilMoistureValue = null;
        if (soilMoisture && soilMoisture.length > 0) {
          soilMoisture.map((item) => {
            if (item !== null && soilMoistureValue === null) {
              soilMoistureValue = item.avg;
            }
          });
        }

        let soilTemperatureValue = null;
        if (soilTemperature && soilTemperature.length > 0) {
          soilTemperature.map((item) => {
            if (item !== null && soilTemperatureValue === null) {
              soilTemperatureValue = Math.round(item.avg * 100) / 100 ;
            }
          });
        }

        acc.push({
          key: `key-${id}`,
          device: location.name || noDataLabel,
          temperature: temperature || noDataLabel,
          gdd: gdd && gdd.value !== null ? gdd.value.toFixed(0) : noDataLabel,
          dewDuration: dewDurationValue || noDataLabel,
          humidity: humidity ? humidity.value : noDataLabel,
          dew: dew ? dew.value : noDataLabel,
          ndvi:
            ndvi && ndvi.value !== null && ndvi.value !== noDataLabel
              ? ndvi.value === 0
                ? 0
                : ndvi.value.toFixed(2)
              : noDataLabel,
          metrics: [
            {
              id: `temperature`,
              label: MetricsLabels.temperature,
              value: temperatureAvg ? `${temperatureAvg > 0 ? '+' : ''}${Math.round(temperatureAvg)}°C` : null,
              valueSecondary:
                temperatureMin && temperatureMax
                  ? `${temperatureMin > 0 ? '+' : ''}${Math.round(temperatureMin)}...${temperatureMax > 0 ? '+' : ''}${Math.round(temperatureMax)}°C`
                  : null,
              icon: TemperatureIcon,
            },
            {
              id: `gdd`,
              label: MetricsLabels.gdd,
              value: gdd && gdd.value !== noDataLabel ? `${Math.round(gdd.value)}` : null,
              valueSecondary: null,
              icon: GddIcon,
            },
            {
              id: `dewDuration`,
              label: MetricsLabels.dewfallDuration,
              value: dewDurationValue,
              valueSecondary: dewDurationSecondaryValue,
              icon: DewDurationIcon,
            },
            {
              id: `humidity`,
              label: MetricsLabels.humidity,
              labelMobile: MetricsLabels.humidityUnits,
              value: humidity ? humidity.value : null,
              valueSecondary: null,
              icon: HumidityIcon,
            },
            {
              id: `dew`,
              label: MetricsLabels.dewPoint,
              value: dew ? dew.value : null,
              valueSecondary: null,
              icon: DewIcon,
            },
            {
              id: `ndvi`,
              label: MetricsLabels.ndvi,
              value: ndvi && ndvi.value !== null ? Math.round(ndvi.value * 100) / 100 : null,
              valueSecondary: null,
              icon: NdviIcon,
            },
            {
              id: `soilTemperature`,
              label: MetricsLabels.soilTemperature,
              value: soilTemperatureValue,
              valueSecondary: null,
              icon: SoilTemperatureIcon,
            },
            {
              id: `soilMoisture`,
              label: MetricsLabels.soilMoisture,
              value: soilMoistureValue,
              valueSecondary: null,
              icon: SoilMoistureIcon,
            },
          ],
        });
      }
      return acc;
    }, []);

    setData(dataSource);
  }, [sensor, sensorCompared, selectedSecondarySensor]);

  return (
    <>
      {data && data.length === 1 && (
        <div className={styles.metricsGrid}>
          {data[0].metrics.map((metric) => {
            return (
              (showSoilData || (!showSoilData && metric.id !== 'soilMoisture' && metric.id !== 'soilTemperature')) && (
                <MetricCard
                  key={metric.id}
                  id={metric.id}
                  label={metric.label}
                  labelMobile={metric.labelMobile || ''}
                  value={metric.value}
                  valueSecondary={metric.valueSecondary}
                  icon={metric.icon}
                  unit={metric.unit}
                  isActive={activeReportChart === metric.id}
                  clickCard={() => onClickCard(metric.id)}
                  t={t}
                />
              )
            );
          })}
        </div>
      )}

      {data && data.length > 1 && (
        <Table className={styles.metricsTable} columns={MetricsColumns} dataSource={data} pagination={false} />
      )}
    </>
  );
};

export default Metrics;
